import { URL_PATTERNS } from '../_constants/urlConstants';

/**
 * Converts a tab object to its URL pathname.
 * Returns null if the tab type has no URL mapping or if required
 * parameters (projectId, docId) are missing or zero.
 *
 * @param {Object} tab - A mainTabs tab object
 * @returns {string|null}
 */
export function tabToPath(tab) {
  if (!tab) return null;

  // Iterate all patterns for this docType and return the first one that resolves.
  // This handles docTypes with multiple patterns (e.g. OVERTIME_LIST has both
  // /projects/:projectId/overtime and /overtime) — whichever satisfies the
  // available tab params wins.
  for (const pattern of URL_PATTERNS) {
    if (pattern.docType !== tab.docType) continue;

    let path = pattern.path;

    if (path.includes(':projectId')) {
      if (!tab.projectId) continue; // Try next pattern for this docType
      path = path.replace(':projectId', tab.projectId);
    }

    if (path.includes(':docId')) {
      // docId of 0 means the document hasn't been saved yet — don't encode it
      if (!tab.docId) continue; // Try next pattern for this docType
      path = path.replace(':docId', tab.docId);
    }

    return path; // First pattern that fully resolves
  }

  return null;
}

/**
 * Parses a URL pathname against URL_PATTERNS and returns tab parameters.
 * Returns null if no pattern matches.
 *
 * @param {string} pathname
 * @returns {{ docType: string, projectId: number|null, docId: number|null }|null}
 */
export function matchPathToTab(pathname) {
  if (!pathname || pathname === '/') return { docType: 'HOME', projectId: null, docId: null };

  for (const pattern of URL_PATTERNS) {
    const params = _matchSegments(pathname, pattern.path);
    if (params !== null) {
      return {
        docType: pattern.docType,
        projectId: params.projectId != null ? parseInt(params.projectId, 10) : null,
        docId: params.docId != null ? parseInt(params.docId, 10) : null,
      };
    }
  }

  return null;
}

/**
 * Finds an existing open tab that matches the parsed URL parameters.
 * Uses the same matching logic as the actionScript singleton checks.
 *
 * @param {Object[]} openTabs
 * @param {{ docType: string, projectId: number|null, docId: number|null }} match
 * @returns {Object|null}
 */
export function findMatchingTab(openTabs, match) {
  if (!match) return null;

  return openTabs.find(tab => {
    if (tab.docType !== match.docType) return false;
    if (match.projectId != null && tab.projectId !== match.projectId) return false;
    if (match.docId != null && tab.docId !== match.docId) return false;
    return true;
  }) || null;
}

/**
 * Matches a pathname against a pattern string with :param segments.
 * Returns an object of extracted param values, or null if no match.
 *
 * @param {string} pathname - e.g. "/projects/42/adrs/7"
 * @param {string} pattern  - e.g. "/projects/:projectId/adrs/:docId"
 * @returns {Object|null}
 */
function _matchSegments(pathname, pattern) {
  const patternParts = pattern.split('/');
  const pathParts = pathname.split('/');

  if (patternParts.length !== pathParts.length) return null;

  const params = {};
  for (let i = 0; i < patternParts.length; i++) {
    if (patternParts[i].startsWith(':')) {
      params[patternParts[i].slice(1)] = pathParts[i];
    } else if (patternParts[i] !== pathParts[i]) {
      return null;
    }
  }

  return params;
}
